import { useEffect, useRef, useState } from "react";
import { BEACON_BYTES, SESSION_TTL_MS } from "@psplit/ledger-core";
import { Modal } from "./Modal.js";
import { broadcastBeacon, listenForBeacon, type ListenStatus } from "../lib/nearbyAudio.js";
import {
  joinNearby,
  NearbyHost,
  type JoinProgress,
  type PendingJoiner,
} from "../lib/nearbySession.js";
import { errorMessage } from "../lib/errors.js";

// Both halves wait for an explicit tap before touching the speaker or the
// microphone. iOS will not start an AudioContext outside a gesture, and asking
// for the mic the instant a screen opens is the kind of thing that gets an app
// declined forever.

function secondsLeft(startedAt: number): number {
  return Math.max(0, Math.ceil((startedAt + SESSION_TTL_MS - Date.now()) / 1000));
}

export function NearbyHostModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [session, setSession] = useState<NearbyHost | null>(null);
  const [pending, setPending] = useState<PendingJoiner[]>([]);
  const [left, setLeft] = useState(0);
  const [error, setError] = useState("");
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abort.current?.abort();
  }, []);

  useEffect(() => {
    if (!session) return;
    let live = true;
    const tick = async () => {
      while (live && !session.expired()) {
        try {
          await session.poll();
          if (!live) return;
          setPending([...session.pending]);
          setLeft(secondsLeft(session.startedAt));
        } catch (e) {
          if (live) setError(errorMessage(e));
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      if (live) {
        setLeft(0);
        abort.current?.abort();
      }
    };
    void tick();
    return () => {
      live = false;
    };
  }, [session]);

  const start = () => {
    try {
      const s = NearbyHost.start(id);
      const controller = new AbortController();
      abort.current = controller;
      void broadcastBeacon(s.beacon, controller.signal);
      setLeft(secondsLeft(s.startedAt));
      setSession(s);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const act = async (joiner: PendingJoiner, approve: boolean) => {
    if (!session) return;
    if (approve) await session.approve(joiner.eph);
    else session.decline(joiner.eph);
    setPending([...session.pending]);
  };

  return (
    <Modal onClose={onClose}>
      <h3>Add people nearby</h3>
      {!session ? (
        <>
          <p className="fineprint">
            Your phone plays a short sound that people cannot hear but phones can. Anyone at the
            table who is listening will ask to join, and you decide who gets in. The sound carries
            no group key: it is only a pointer, so someone at the next table who happens to be
            listening still cannot read anything.
          </p>
          <button className="btn" onClick={start}>
            Start the sound
          </button>
        </>
      ) : (
        <>
          <p className="fineprint">
            {left > 0
              ? `Playing. Ask them to open PaisaSplit and tap "join a group nearby". ${left}s left.`
              : "The sound has stopped. Close and start again if someone still needs to join."}
          </p>
          {pending.length === 0 && left > 0 && <p className="fineprint">Nobody has asked yet.</p>}
          {pending.map((j) => (
            <div className="card" key={j.eph}>
              <strong>{j.name === "" ? "Someone" : j.name}</strong>
              <div className="stamp">code {j.code}</div>
              {j.state === "waiting" && (
                <>
                  <p className="fineprint">
                    Check their screen shows <strong>{j.code}</strong> before you let them in. If it
                    does not, this is not the person in front of you.
                  </p>
                  <button className="btn" onClick={() => void act(j, true)}>
                    Let them in
                  </button>
                  <button className="btn danger" onClick={() => void act(j, false)}>
                    No
                  </button>
                </>
              )}
              {j.state === "approving" && <p className="fineprint">Adding...</p>}
              {j.state === "added" && <p className="fineprint">Added to the group.</p>}
              {j.state === "declined" && <p className="fineprint">Turned away.</p>}
              {j.state === "error" && <p className="fineprint">Could not add them: {j.error}</p>}
            </div>
          ))}
        </>
      )}
      {error !== "" && <div className="notice">{error}</div>}
      <button className="btn" onClick={onClose}>
        Done
      </button>
    </Modal>
  );
}

const LISTEN_COPY: Record<ListenStatus, string> = {
  asking: "Waiting for microphone permission...",
  listening: "Listening for the other phone...",
  heard: "Heard it.",
  denied: "Microphone permission was refused, so this cannot work. You can still join by QR.",
  timeout: "Heard nothing. Ask them to start the sound again, and keep the phones close.",
  unsupported: "This browser will not give us the microphone. Join by QR or invite link instead.",
};

const JOIN_COPY: Record<JoinProgress["stage"], string> = {
  "looking-for-host": "Found the sound. Reaching the host...",
  "wrong-host": "Something else is answering on this channel. Ignoring it.",
  "waiting-for-approval": "Asked to join. Waiting for them to let you in.",
  joined: "You are in.",
  "timed-out": "That did not finish in time. Ask them to start again.",
};

export function NearbyJoinModal({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<"idle" | "listening" | "joining" | "done">("idle");
  const [listen, setListen] = useState<ListenStatus>("asking");
  const [progress, setProgress] = useState<JoinProgress | null>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abort.current?.abort();
  }, []);

  const start = async () => {
    const controller = new AbortController();
    abort.current = controller;
    setPhase("listening");
    const heard = await listenForBeacon(BEACON_BYTES, controller.signal, setListen);
    if (controller.signal.aborted) return;
    if (heard.status !== "heard" || !heard.payload) {
      setPhase("done");
      return;
    }
    setPhase("joining");
    const result = await joinNearby(heard.payload, controller.signal, setProgress);
    setProgress(result);
    setPhase("done");
  };

  return (
    <Modal onClose={onClose}>
      <h3>Join a group nearby</h3>
      {phase === "idle" && (
        <>
          <p className="fineprint">
            Ask whoever runs the group to start the sound on their phone. We will listen for it
            with the microphone, then ask them to let you in. We do not record anything: the audio
            is read for a tone and thrown away.
          </p>
          <button className="btn" onClick={() => void start()}>
            Start listening
          </button>
        </>
      )}
      {phase === "listening" && <p className="fineprint">{LISTEN_COPY[listen]}</p>}
      {(phase === "joining" || phase === "done") && progress !== null && (
        <>
          <p className="fineprint">{JOIN_COPY[progress.stage]}</p>
          {progress.code !== undefined && progress.stage !== "joined" && (
            <>
              <div className="stamp">code {progress.code}</div>
              <p className="fineprint">Show this to them so they know it is really you.</p>
            </>
          )}
          {progress.stage === "joined" && progress.groupName !== undefined && (
            <p className="fineprint">Joined {progress.groupName}.</p>
          )}
          {progress.error !== undefined && <div className="notice">{progress.error}</div>}
        </>
      )}
      {phase === "done" && progress === null && <p className="fineprint">{LISTEN_COPY[listen]}</p>}
      <button className="btn" onClick={onClose}>
        {phase === "done" ? "Done" : "Cancel"}
      </button>
    </Modal>
  );
}
